import type { ShopLocationDto } from "@babackapp/private-api-client/generated/schema";
import FormCountrySelector from "@babackapp/react-hook-forms-components/form-country-selector";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { cn } from "@/utils/cn";

const formSchema = z.object({
  isDefault: z.boolean(),
  address1: z.string(),
  address2: z.string(),
  city: z.string(),
  country: z.object({
    name: z.string(),
    code: z.string(),
  }),
  province_code: z.string().optional(),
  zip: z.string(),
  companyName: z.string(),
  phone: z.string().optional(),
});

export type LocationFormValues = z.infer<typeof formSchema>;

type LocationFormProps = {
  defaultValues?: z.infer<typeof formSchema>;
  onSubmit: (data: ShopLocationDto) => void;
  onRemove?: () => void;
};

const DEFAULT_VALUES = {
  isDefault: false,
  address1: "",
  address2: "",
  city: "",
  zip: "",
  companyName: "",
  province_code: "",
  phone: "",
  country: {
    name: "France",
    code: "FR",
  },
};

export default function LocationForm({
  defaultValues = DEFAULT_VALUES,
  onSubmit,
  onRemove,
}: LocationFormProps) {
  const t = useTranslation();

  const form = useForm<LocationFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const _onSubmit = form.handleSubmit((data) => {
    onSubmit({
      isDefault: data.isDefault,
      address: {
        address1: data.address1,
        /**
         * TODO: optional in API
         */
        address2: data.address2 || "-",
        city: data.city,
        country_code: data.country.code,
        country_name: data.country.name,
        country: data.country.name,
        zip: data.zip,
        companyName: data.companyName,
        province_code: data.province_code,
        phone: data.phone,
      },
    });
  });

  return (
    <Form {...form}>
      <form
        onSubmit={_onSubmit}
        className="flex flex-col justify-start gap-8 mt-[16px]"
      >
        <FormField
          control={form.control}
          name="companyName"
          render={({ field }) => (
            <FormInput
              label={t("settings.shipping.fields.companyName.title")}
              description={t(
                "settings.shipping.fields.companyName.description",
              )}
              {...field}
            />
          )}
        />

        <FormInputRow>
          <FormField
            control={form.control}
            name="address1"
            render={({ field }) => (
              <FormInput
                label={t("settings.shipping.fields.address1.title")}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="address2"
            render={({ field }) => (
              <FormInput
                label={t("settings.shipping.fields.address2.title")}
                {...field}
              />
            )}
          />
        </FormInputRow>

        <FormInputRow>
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormInput
                label={t("settings.shipping.fields.city.title")}
                {...field}
              />
            )}
          />

          <FormField
            control={form.control}
            name="zip"
            render={({ field }) => (
              <FormInput
                label={t("settings.shipping.fields.zip.title")}
                {...field}
              />
            )}
          />
        </FormInputRow>

        <FormInputRow>
          <FormField
            control={form.control}
            name="province_code"
            render={({ field }) => (
              <FormInput
                label={t("settings.shipping.fields.provinceCode.title")}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormInput
                label={t("settings.shipping.fields.phone.title")}
                {...field}
              />
            )}
          />
        </FormInputRow>

        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormCountrySelector
              label={t("settings.shipping.fields.country.title")}
              value={field.value}
              onValueChange={field.onChange}
            />
          )}
        />

        <FormField
          control={form.control}
          name="isDefault"
          render={({ field }) => (
            <FormSwitch
              label={t("settings.shipping.fields.isDefault.title")}
              description={t("settings.shipping.fields.isDefault.description")}
              {...field}
            />
          )}
        />

        <div
          className={cn(
            "flex items-center gap-2",
            onRemove ? "justify-between" : "justify-center",
          )}
        >
          {onRemove && (
            <Button
              variant="destructive-outline"
              onClick={onRemove}
              type="button"
            >
              {t("misc.delete")}
            </Button>
          )}
          <FormSaveButton />
        </div>
      </form>
    </Form>
  );
}
